"use client";

import { useAction, useQuery } from "convex/react";
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import type { BillingCurrency } from "../../../convex/lib/billingRail";
import { friendlyConvexError } from "@/studio/lib/convexUserErrors";
import type { CheckoutPaypalSession } from "./UnifiedCheckout";
import { PaymentProcessingOverlay, type PaymentConfirmedLabel, type PaymentOverlayView } from "./PaymentProcessingOverlay";
import { friendlyPaymentError, isPaymentCancel } from "./paymentErrors";

/** Mounts the overlay on document.body while a view is set, independent of where the hook lives. */
function usePaymentOverlay(view: PaymentOverlayView | null, onDismiss: () => void) {
  const rootRef = useRef<{ root: Root; host: HTMLElement } | null>(null);
  const open = view !== null;
  useEffect(() => {
    if (!open) return;
    const host = document.createElement("div");
    host.dataset.paymentProcessingHost = "";
    document.body.appendChild(host);
    rootRef.current = { root: createRoot(host), host };
    return () => {
      const current = rootRef.current;
      rootRef.current = null;
      if (!current) return;
      // Unmount after the commit so React does not warn about synchronous unmount during render.
      queueMicrotask(() => {
        current.root.unmount();
        current.host.remove();
      });
    };
  }, [open]);
  useEffect(() => {
    rootRef.current?.root.render(createElement(PaymentProcessingOverlay, { view, onDismiss }));
  }, [view, onDismiss]);
}

function usdLabel(usdValue: string | null | undefined): string {
  const n = Number(usdValue);
  if (!usdValue || !Number.isFinite(n)) return "";
  return `US$${n.toLocaleString("en-US", { minimumFractionDigits: n % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

/** What the PayPal order is for. Exactly one shape per checkout. */
export type CheckoutPaypalOrder =
  | { kind: "topup"; amountCents: number; reference?: string; academyCourseId?: Id<"academyCourses"> }
  | { kind: "subscribe"; planId: Id<"subscriptionPlans">; interval: "month" | "year" };

export function newCheckoutRequestId(prefix: string): string {
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return `${prefix}-${Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * One checkout payment flow for every UnifiedCheckout (sign-up, Billing plans,
 * Billing top-up, Settings top-up, Academy).
 *
 * PayPal (USD): the order and its pending payment row are created lazily, only
 * when the buyer submits card fields or clicks the PayPal button. Capture runs
 * server-side; the webhook stays the backstop.
 * Wam (TTD): `payTtd` runs the caller's redirect/handoff with a stable request id.
 */
export function useCheckoutPayment(opts: {
  preview?: boolean;
  currency: BillingCurrency;
  /** Disable PayPal (for example Google Play builds). */
  paypalDisabled?: boolean;
  requestPrefix: string;
  /** Changing this (plan, interval, amount) starts a fresh request id. */
  requestKey: string;
  /** Validate and describe the order. Return a string to block with that message. */
  order: () => CheckoutPaypalOrder | string;
  /** Wam/TTD start. Receives a stable request id. Throw to report an error. */
  startTtd?: (clientRequestId: string) => Promise<void>;
  onMessage: (message: string, tone: "error" | "info") => void;
  pendingMessage?: string;
  ttdErrorMessage?: string;
  /** Line under "Payment confirmed", e.g. "Studio Pro, monthly. US$29". Defaults from the order. */
  confirmedDetail?: (amount: string) => string;
  /**
   * Where the Payment confirmed button goes. "signin": paid during sign-up, the button reads
   * "Sign in" and onPaid takes them into Studio. "invoice" (default): already in Studio, the
   * button reads "View invoice" and onPaid receives the payment id so it can open that invoice.
   */
  confirmedAction?: "signin" | "invoice";
  onPaid: (paymentId: Id<"payments"> | null) => void;
}) {
  const live = !opts.preview && !opts.paypalDisabled;
  /** Preview still mounts the real (sandbox) card fields so they can be typed in; it never creates an order. */
  const showFields = !opts.paypalDisabled;
  const config = useQuery(api.paypalConfig.clientConfig, showFields ? {} : "skip");
  const startPaypal = useAction(api.paypalActions.startPaypalCheckout);
  const capturePaypal = useAction(api.paypalActions.capturePaypalOrder);
  const [busy, setBusy] = useState(false);
  /** True from PayPal click / card submit / Wam start until server confirmation or a real failure. */
  const [view, setView] = useState<PaymentOverlayView | null>(null);
  const processing = view?.phase === "processing";
  const setProcessing = useCallback((on: boolean) => {
    setView((prev) => (on ? { phase: "processing" } : prev?.phase === "processing" ? null : prev));
  }, []);
  const paidRef = useRef(false);
  const paidPaymentIdRef = useRef<Id<"payments"> | null>(null);
  const dismissOverlay = useCallback(() => {
    setView(null);
    if (paidRef.current) {
      paidRef.current = false;
      const paymentId = paidPaymentIdRef.current;
      paidPaymentIdRef.current = null;
      optsRef.current.onPaid(paymentId);
    }
  }, []);
  const [locked, setLocked] = useState<{ usdValue: string; fxUsdToTtd: number } | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const lockedRef = useRef<{ usdValue: string; fxUsdToTtd: number } | null>(null);
  lockedRef.current = locked;
  const processingRef = useRef(false);
  processingRef.current = processing;
  const orderRef = useRef<{ paymentId: Id<"payments">; orderId: string } | null>(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;
  usePaymentOverlay(view, dismissOverlay);
  /** A real failure while processing replaces the spinner with the failed view; otherwise a message. */
  const failWith = useCallback((message: string | null | undefined, actionLabel: "Try again" | "Close" = "Try again") => {
    const text = (message ?? "").trim();
    setView((prev) => (prev?.phase === "processing" && text ? { phase: "error", message: text, actionLabel } : prev?.phase === "processing" ? null : prev));
  }, []);
  /** Never surface an empty toast or status line. */
  const say = useCallback((message: string | null | undefined, tone: "error" | "info") => {
    const text = (message ?? "").trim();
    if (!text) return;
    optsRef.current.onMessage(text, tone);
  }, []);

  const reset = useCallback(() => {
    requestIdRef.current = null;
    orderRef.current = null;
    setLocked(null);
  }, []);

  useEffect(() => {
    reset();
  }, [opts.requestKey, opts.currency, reset]);

  const requestId = useCallback(() => {
    if (!requestIdRef.current) {
      requestIdRef.current = newCheckoutRequestId(optsRef.current.requestPrefix);
    }
    return requestIdRef.current;
  }, []);

  const createOrder = useCallback(async (): Promise<string> => {
    const o = optsRef.current;
    const spec = o.order();
    if (typeof spec === "string") {
      say(spec, "error");
      throw new Error(spec);
    }
    // No overlay here: PayPal calls createOrder on click, and the guest card form opens inline below
    // the button. The overlay goes up only in onApprove, while we capture.
    try {
      const result = await startPaypal({
        clientRequestId: requestId(),
        origin: window.location.origin,
        currency: "USD",
        ...(spec.kind === "subscribe"
          ? { planId: spec.planId, interval: spec.interval }
          : {
              amountCents: spec.amountCents,
              reference: spec.reference,
              academyCourseId: spec.academyCourseId,
            }),
      });
      orderRef.current = { paymentId: result.paymentId, orderId: result.orderId };
      setLocked({ usdValue: result.usdValue, fxUsdToTtd: result.fxUsdToTtd });
      return result.orderId;
    } catch (error) {
      requestIdRef.current = null;
      failWith(friendlyConvexError(error, "Could not start PayPal checkout."));
      throw error;
    }
  }, [startPaypal, requestId, failWith]);

  const onApprove = useCallback(
    async (orderId?: string) => {
      const o = optsRef.current;
      const current = orderRef.current;
      if (!current || (orderId && orderId !== current.orderId)) {
        setProcessing(false);
        say("PayPal is still confirming.", "info");
        return;
      }
      setProcessing(true);
      let result: Awaited<ReturnType<typeof capturePaypal>>;
      try {
        result = await capturePaypal({ paymentId: current.paymentId, orderId: current.orderId });
      } catch (error) {
        failWith(friendlyConvexError(error, "We could not confirm your payment. If you were charged, it will show in Billing shortly."), "Close");
        return;
      }
      if (result.status === "payment_completed") {
        const spec = o.order();
        const amount = usdLabel(lockedRef.current?.usdValue);
        const detail = o.confirmedDetail
          ? o.confirmedDetail(amount)
          : typeof spec === "string"
            ? amount
            : spec.kind === "subscribe"
              ? `Studio plan, billed ${spec.interval === "year" ? "yearly" : "monthly"}${amount ? `. ${amount}` : ""}`
              : `Top-up${amount ? ` of ${amount}` : ""}`;
        reset();
        paidRef.current = true;
        paidPaymentIdRef.current = current.paymentId;
        setView({
          phase: "success",
          detail,
          actionLabel: o.confirmedAction === "signin" ? "Sign in" : "View invoice",
        });
      } else {
        setProcessing(false);
        say(result.reason || o.pendingMessage || "PayPal is still confirming.", "info");
      }
    },
    [capturePaypal, reset, say, failWith],
  );

  /** Buyer closed the PayPal popup or cancelled: quietly return to checkout. */
  const onCancel = useCallback(() => {
    setProcessing(false);
  }, []);

  /** Raw PayPal SDK errors. Popup-closed is a cancel; everything else gets friendly copy. */
  const onPaypalError = useCallback(
    (error: unknown) => {
      if (isPaymentCancel(error)) {
        setProcessing(false);
        return;
      }
      const message = friendlyPaymentError(error);
      if (processingRef.current) failWith(message);
      else say(message, "error");
    },
    [say, failWith],
  );

  const payTtd = useCallback(async () => {
    const o = optsRef.current;
    if (!o.startTtd || busy) return;
    if (o.currency !== "TTD") {
      o.onMessage("PayPal is still loading. Try again in a moment.", "info");
      return;
    }
    setBusy(true);
    setProcessing(true);
    try {
      await o.startTtd(requestId());
    } catch (error) {
      requestIdRef.current = null;
      if (isPaymentCancel(error)) setProcessing(false);
      else failWith(friendlyConvexError(error, o.ttdErrorMessage ?? "Could not start checkout."));
    } finally {
      setProcessing(false);
      setBusy(false);
    }
  }, [busy, requestId, failWith, setProcessing]);

  const paypal: CheckoutPaypalSession | null = useMemo(() => {
    if (!showFields || opts.currency !== "USD" || !config?.clientId) return null;
    if (!live) {
      const blocked = "Preview only. No payment is sent.";
      return {
        clientId: config.clientId,
        createOrder: async () => {
          optsRef.current.onMessage(blocked, "info");
          throw new Error(blocked);
        },
        onApprove: async () => undefined,
        onCancel: () => undefined,
        onError: (error: unknown) => {
          if (isPaymentCancel(error)) return;
          say(friendlyPaymentError(error, blocked), "info");
        },
      };
    }
    return {
      clientId: config.clientId,
      createOrder,
      onApprove,
      onCancel,
      onError: onPaypalError,
    };
  }, [live, showFields, opts.currency, config?.clientId, createOrder, onApprove, onCancel, onPaypalError, say]);

  return {
    paypal,
    payTtd,
    busy,
    /** A payment is being processed or confirmed; the blocking overlay is up. */
    processing,
    /** Locked live FX once a PayPal order exists. */
    lockedFxUsdToTtd: locked?.fxUsdToTtd ?? null,
    lockedUsdValue: locked?.usdValue ?? null,
    reset,
  };
}

/** "Studio Pro, billed monthly. US$286" for the Payment confirmed line. */
export function planConfirmedDetail(planName: string | null | undefined, interval: "month" | "year", amount: string): string {
  const raw = (planName ?? "").trim();
  const name = !raw ? "Studio plan" : /^studio\b/i.test(raw) ? raw : `Studio ${raw}`;
  return `${name}, billed ${interval === "year" ? "yearly" : "monthly"}${amount ? `. ${amount}` : ""}`;
}

/** "Top-up of US$50" for the Payment confirmed line. */
export function topUpConfirmedDetail(amount: string): string {
  return amount ? `Top-up of ${amount}` : "Top-up";
}
